import { AdmPlan } from "@custom-types/base-types";
import { SCENE_SEAT_IDENTIFIER } from "@components/plans-table/scene-licenses";
import {
  AllHoloBuilderLegacyPlans,
  AllSphereXg2026Bundles,
  AllSubscriptions,
} from "@utils/product/product-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

/** This is the default user Id that is added as createdBy for plans that are created automatically */
export const DEFAULT_USER_ID = "00000000-0000-0000-0000-000000000000";

export const SCENE_SEATS_CANNOT_EXCEED_PLAN_LIMIT_ERROR =
  "The total number of SCENE seats must not exceed the number of SCENE seats allowed by the plan.";

/**
 * Calculates the number of used seats for a SCENE Seats plan.
 * A used seat is defined as a plan that is not assigned to the default user.
 *
 * @param plans - An array of AdmPlan objects to evaluate.
 * @returns The number of used seats (plans not assigned to the default user).
 */
export function calculateSceneUsedSeats(plans: AdmPlan[]): number {
  return plans.reduce((acc, plan) => {
    if (plan.subject.id !== DEFAULT_USER_ID) {
      return acc + 1;
    }
    return acc;
  }, 0);
}

/**
 * Bundles that make a plan one of the Sphere subscriptions a workspace is sold:
 * the 2025 Sphere XG plans and the 2026 Scan plans that replace them.
 */
const SPHERE_PLAN_BUNDLES: readonly string[] = [
  ...AllSubscriptions,
  ...AllSphereXg2026Bundles,
];

/**
 * Bundles that make a plan a subscription, i.e. a product a workspace is sold to get access to
 * the platform. Next to the Sphere plans of every year those are the legacy HoloBuilder plans,
 * which are modelled as bundles but are still offered as subscriptions.
 */
const SUBSCRIPTION_BUNDLES: readonly string[] = [
  ...SPHERE_PLAN_BUNDLES,
  ...AllHoloBuilderLegacyPlans,
];

/** Whether the plan grants at least one of the given bundles */
export function grantsAnyBundle(
  plan: AdmPlan,
  bundles: readonly string[]
): boolean {
  return plan.featureBundles.some(({ identifier }) =>
    bundles.includes(identifier)
  );
}

/**
 * Whether the plan grants the given feature, either directly or through one of its bundles.
 * A bundle reports the features it includes, so a plan can grant a feature without listing it
 * among its own features.
 */
export function grantsFeature(plan: AdmPlan, feature: string): boolean {
  return (
    plan.features.some(({ identifier }) => identifier === feature) ||
    plan.featureBundles.some(({ features }) =>
      features.some(({ identifier }) => identifier === feature)
    )
  );
}

/**
 * Whether the plan is a token/credits plan, i.e. one of the products the "Add Subscription" page
 * offers under its "Tokens" section. Those plans carry no feature or bundle identifier at all,
 * only a CREDITS constraint, so they need to be recognized separately from `grantsAnyBundle`.
 */
export function isTokenPlan(plan: AdmPlan): boolean {
  return plan.constraints.some(({ type }) => type === "CREDITS");
}

/**
 * Whether the plan grants the tokens of a subscription, i.e. a token plan that was created
 * together with a plan and attached to it. Those are not listed next to the plan they belong to:
 * the plan reports its tokens itself.
 */
export function isAttachedTokenPlan(plan: AdmPlan): boolean {
  return isTokenPlan(plan) && Boolean(plan.metadata?.parentSubscriptionId);
}

/** Whether the plan grants a single SCENE seat, instead of being a plan a workspace is sold */
export function isSceneSeatPlan(plan: AdmPlan): boolean {
  return plan.features.some(
    ({ identifier }) => identifier === SCENE_SEAT_IDENTIFIER
  );
}

/** The constraint limiting how many SCENE seats the plan allows, unless the plan predates it */
export function getSceneSeatConstraint(
  plan: AdmPlan
): FaroCoreApiTypes.BasicLimit | undefined {
  return plan.constraints.find(({ type }) => type === "SCENE_SEAT");
}

/** Whether the plan is one of the Sphere subscriptions, i.e. a 2025 Sphere XG plan or a 2026 Scan plan */
export function isSpherePlan(plan: AdmPlan): boolean {
  return grantsAnyBundle(plan, SPHERE_PLAN_BUNDLES);
}

/**
 * Whether the plan is one of the subscriptions a workspace is sold, i.e. one of the products the
 * "Add Subscription" page offers. The trial plans count as well, as they grant a Sphere XG bundle.
 * Token/credits plans count too: they carry no feature bundle, only a CREDITS constraint.
 */
export function isSubscriptionPlan(plan: AdmPlan): boolean {
  return grantsAnyBundle(plan, SUBSCRIPTION_BUNDLES) || isTokenPlan(plan);
}

/**
 * Whether the plan grants an add-on on top of a subscription, i.e. one of the individual features
 * or bundles the "Add Feature" page offers.
 *
 * A plan that grants nothing at all is not counted, and neither are token plans: they only carry
 * a credits limit and are sold from "Add Subscription", not "Add Feature".
 */
export function isFeaturePlan(plan: AdmPlan): boolean {
  return (
    !isSubscriptionPlan(plan) &&
    (plan.features.length > 0 || plan.featureBundles.length > 0)
  );
}

/**
 * Feature identifier granted to a workspace whose HoloBuilder data was migrated to Sphere XG.
 * A plan can grant it on its own, or through the bundle of the same name that includes it.
 */
export const HB_TO_SXG_MIGRATED_IDENTIFIER: FaroCoreApiTypes.FeatureIdentifier =
  "hb-to-sxg-migrated";

/**
 * Whether the workspace has been migrated from HoloBuilder to Sphere XG, which is the case when any
 * of its plans that are not deactivated grants the `hb-to-sxg-migrated` feature, either directly or
 * through one of its bundles.
 */
export function isWorkspaceHbToSxgMigrated(plans: AdmPlan[]): boolean {
  return plans.some(
    (plan) =>
      plan.status.identifier !== "deactivated" &&
      grantsFeature(plan, HB_TO_SXG_MIGRATED_IDENTIFIER)
  );
}
